'use client';

import React from 'react';
import { ArrowRight, Check, Star } from 'lucide-react';

interface Props {
  selectedPackage: string;
  selectedAutoship: string;
  onSelectPackage: (pkg: string) => void;
  onSelectAutoship: (auto: string) => void;
  onNext: () => void;
}

const packages = [
  {
    id: 'pkg-core',
    name: 'Core',
    price: '$295',
    pv: '',
    tag: null,
    sleeves: '3 Sleeves of X39',
    description: 'Entry-level Brand Partner enrollment. Get started with wholesale pricing and your business kit.',
    features: [
      'Brand Partner enrollment',
      'Wholesale pricing access',
      '3 sleeves of X39 (sample)',
      'Business kit included',
    ],
    borderColor: 'rgba(255,255,255,0.1)',
    bg: 'rgba(255,255,255,0.03)',
  },
  {
    id: 'pkg-advanced',
    name: 'Advanced',
    price: '$535',
    pv: '',
    tag: null,
    sleeves: '6 Sleeves of X39',
    description: 'More product volume and enhanced business tools. Recommended for serious builders.',
    features: [
      'Brand Partner enrollment',
      'Enhanced business tools',
      '6 sleeves of X39',
      'Faster rank qualification',
    ],
    borderColor: 'rgba(0,168,255,0.2)',
    bg: 'rgba(0,168,255,0.04)',
  },
  {
    id: 'pkg-advanced-plus',
    name: 'Advanced Plus',
    price: '$975',
    pv: '',
    tag: null,
    sleeves: '11 Sleeves of X39',
    description: 'Ignite the power of X39 as a business. Maximum product variety and premium resources.',
    features: [
      'Premium business resources',
      '11 sleeves of X39',
      'Fast-track qualification',
      'Priority support access',
    ],
    borderColor: 'rgba(0,229,255,0.25)',
    bg: 'rgba(0,229,255,0.04)',
  },
  {
    id: 'pkg-premium',
    name: 'Premium',
    price: '$1,750',
    pv: '',
    tag: 'BEST VALUE',
    sleeves: '20 Sleeves of X39',
    description: 'For the serious entrepreneur. Maximum value for rank advancement, full product suite.',
    features: [
      '20 sleeves of X39',
      'Maximum value for rank advancement',
      '2 months subscription included',
      'Reduced to ~$87.50 per sleeve',
    ],
    borderColor: 'rgba(245,158,11,0.4)',
    bg: 'rgba(245,158,11,0.06)',
  },
];

const autoshipOptions = [
  {
    id: 'auto-1',
    label: '1 Sleeve / Month',
    price: '$99.95/month',
    pv: '',
    note: 'Keep your subscription active',
    recommended: false,
  },
  {
    id: 'auto-2',
    label: '2 Sleeves / Month',
    price: '$199.90/month',
    pv: '',
    note: 'Potential to max comp plan',
    recommended: true,
  },
];

export default function Slide7APackages({
  selectedPackage,
  selectedAutoship,
  onSelectPackage,
  onSelectAutoship,
  onNext,
}: Props) {

  const handleNext = () => {
    onNext();
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-4 py-16 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% 10%, rgba(0,168,255,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-5xl w-full mx-auto fade-in">
        <div className="text-center mb-12">
          <p className="text-primary text-sm font-semibold tracking-widest uppercase mb-3">
            Enrollment Packages
          </p>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-foreground leading-tight mb-5">
            LifeWave <span className="gradient-text">Enrollment Packages</span>
          </h2>
          <p className="text-secondary-foreground text-lg max-w-xl mx-auto">
            Choose the package that fits your goals. All include Brand Partner enrollment and wholesale pricing access.
          </p>
        </div>

        {/* Package cards — non-interactive info display */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-10">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className="text-left rounded-2xl p-6 border relative"
              style={{
                background: pkg.bg,
                borderColor: pkg.borderColor,
              }}
            >
              {pkg.tag && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-500 to-amber-400 text-black">
                  {pkg.tag}
                </div>
              )}
              <p className="text-primary font-bold text-sm mb-1">{pkg.name}</p>
              <p className="text-foreground font-extrabold text-3xl tabular-nums mb-1">{pkg.price}</p>
              <p className="text-accent text-sm font-semibold mb-1">{pkg.sleeves}</p>
              <p className="text-secondary-foreground text-xs leading-relaxed mb-4">{pkg.description}</p>
              <ul className="space-y-1.5 mb-3">
                {pkg.features.map((f) => (
                  <li key={`${pkg.id}-feat-${f}`} className="flex items-start gap-1.5 text-xs text-secondary-foreground">
                    <Check size={11} className="text-primary flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground text-xs">+ tax &amp; shipping</p>
            </div>
          ))}
        </div>

        {/* Monthly Autoship — display only */}
        <div className="glass-card p-6 mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Star size={16} className="text-primary" />
            <h3 className="text-foreground font-bold">Monthly Autoship (Required for Wholesale)</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {autoshipOptions.map((opt) => (
              <div
                key={opt.id}
                className="text-left p-4 rounded-xl border relative"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.08)',
                }}
              >
                {opt.recommended && (
                  <span className="block text-xs font-bold text-accent mb-1">Suggested</span>
                )}
                <p className="text-foreground font-semibold text-sm mb-1">{opt.label}</p>
                <p className="text-foreground font-bold text-xl tabular-nums">{opt.price}</p>
                <p className="text-muted-foreground text-xs mt-1">{opt.note}</p>
                <p className="text-muted-foreground text-xs mt-1">+ tax &amp; shipping</p>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-xs mt-3">
            Requires active autoship to maintain wholesale pricing. Autoship can be modified or cancelled at any time.
          </p>
        </div>

        <div className="text-center">
          <button onClick={handleNext} className="btn-primary inline-flex items-center gap-2">
            Ready to Get Started
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}